// src/redux/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie }                   from '../utils/cookies';

const API_BASE = `${process.env.REACT_APP_API_URL}/api/auth`;

// Thunks
export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (userData, thunkAPI) => {
    const res = await fetch(`${API_BASE}/signup`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error);
    return data;
  }
);

export const signInUser = createAsyncThunk(
  'auth/signInUser',
  async (credentials, thunkAPI) => {
    const res = await fetch(`${API_BASE}/signin`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      body: JSON.stringify(credentials),
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error);
    return data;
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (emailData, thunkAPI) => {
    const res = await fetch(`${API_BASE}/forgot-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      body: JSON.stringify(emailData),
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error);
    return data;
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (resetData, thunkAPI) => {
    const res = await fetch(`${API_BASE}/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      body: JSON.stringify(resetData),
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error);
    return data;
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, thunkAPI) => {
    const res = await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error);
    return data;
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:    null,
    status:  'idle',   // 'idle' | 'loading' | 'succeeded' | 'failed'
    error:   null,
    message: null,
  },
  reducers: {
    logout(state) {
      state.user    = null;
      state.status  = 'idle';
      state.error   = null;
      state.message = null;
    },
    clearFirstLogin(state) {
      if (state.user) state.user.firstLogin = false;
    },
    // ← new reducer to wipe stale status/error/message
    clearAuthState(state) {
      state.status  = 'idle';
      state.error   = null;
      state.message = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(signupUser.pending,   state => { state.status='loading'; state.error=null })
      .addCase(signupUser.fulfilled, (state, {payload}) => {
        state.status  = 'succeeded';
        state.message = payload.message;
      })
      .addCase(signupUser.rejected,  (state, action) => {
        state.status = 'failed';
        state.error  = action.payload || action.error.message;
      })

      .addCase(signInUser.pending,   state => { state.status='loading'; state.error=null })
      .addCase(signInUser.fulfilled, (state, {payload}) => {
        state.status  = 'succeeded';
        state.message = payload.message;
        state.user    = payload.user;
      })
      .addCase(signInUser.rejected,  (state, action) => {
        state.status = 'failed';
        state.error  = action.payload || action.error.message;
      })

      .addCase(forgotPassword.pending,   state => { state.status='loading'; state.error=null })
      .addCase(forgotPassword.fulfilled, (state, {payload}) => {
        state.status  = 'succeeded';
        state.message = payload.message;
      })
      .addCase(forgotPassword.rejected,  (state, action) => {
        state.status = 'failed';
        state.error  = action.payload || action.error.message;
      })

      .addCase(resetPassword.pending,   state => { state.status='loading'; state.error=null })
      .addCase(resetPassword.fulfilled, (state, {payload}) => {
        state.status  = 'succeeded';
        state.message = payload.message;
      })
      .addCase(resetPassword.rejected,  (state, action) => {
        state.status = 'failed';
        state.error  = action.payload || action.error.message;
      })

      .addCase(logoutUser.pending, state => {
        state.status = 'loading';
        state.error  = null;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.status  = 'succeeded';
        state.user    = null;
        state.message = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error  = action.payload || action.error.message;
      });
  }
});

export const { logout, clearFirstLogin, clearAuthState } = authSlice.actions;
export default authSlice.reducer;